import React, { useContext, useEffect } from 'react';

import PropTypes from 'prop-types';

import {
  ActivityCard,
  ActivityCardLoader,
} from '@adsk/alloy-react-activity-log';
import { useLocale } from '@adsk/alloy-react-locale';

import ReferenceActivityCardError from './activity/ReferenceActivityCardError';
import ReferenceActivityCardNoPermission from './activity/ReferenceActivityCardNoPermission';
import { getOrCreatePluginContext } from './helpers/pluginContexts';
import { pluginShape } from './helpers/propTypes';
import { useEntitiesCacheContext } from './providers/EntitiesCacheProvider';

const ACTIVITY_VARIANTS = {
  ADDED: 'added',
  REMOVED: 'removed',
};

/**
 * A connected reference activity card component.
 *
 * This component allows you to render an activity card for a specific reference change.
 *
 * This component expects `ReferencePickerProvider` somewhere above it in the components tree.
 */
const ConnectedReferenceActivityCard = React.forwardRef(
  (
    { plugins, entityId, entityType, entityDomain, title: titleProp, ...props },
    ref,
  ) => {
    const plugin = plugins.find(
      (p) =>
        p.metadata.domain === entityDomain && p.metadata.type === entityType,
    );
    const PluginContext = plugin ? getOrCreatePluginContext(plugin) : {};
    const { entitiesCache } = useEntitiesCacheContext();
    const contextValue = useContext(PluginContext);
    const fetchByIds = contextValue?.fetchByIds;
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const title =
      titleProp ||
      (props.variant === ACTIVITY_VARIANTS.REMOVED
        ? T.translate('ACTIVITY_TITLE_REMOVED')
        : T.translate('ACTIVITY_TITLE_ADDED'));

    useEffect(() => {
      if (entityId && fetchByIds) {
        fetchByIds([entityId]);
      }
    }, [entityId, fetchByIds]);

    const { ActivityCard, NoPermissionActivityCard } = plugin || {};
    if (!ActivityCard) {
      return null;
    }
    const entityData = entitiesCache?.[entityDomain]?.[entityType]?.[entityId];
    if (entityData?.loading) {
      return <ActivityCardLoader title={title} {...props} />;
    }
    if (entityData?.error) {
      return (
        <ReferenceActivityCardError
          onTryAgain={entityData.retry}
          title={title}
          {...props}
        />
      );
    }

    if (entityData?.entity) {
      return (
        <ActivityCard
          ref={ref}
          title={title}
          entity={entityData.entity}
          {...props}
        />
      );
    }

    const NoPermissionComponent =
      NoPermissionActivityCard || ReferenceActivityCardNoPermission;
    return <NoPermissionComponent ref={ref} title={title} {...props} />;
  },
);

ConnectedReferenceActivityCard.displayName = 'ConnectedReferenceActivityCard';
ConnectedReferenceActivityCard.VARIANTS = ACTIVITY_VARIANTS;

ConnectedReferenceActivityCard.defaultProps = {
  variant: ACTIVITY_VARIANTS.ADDED,
};

ConnectedReferenceActivityCard.propTypes = {
  ...ActivityCard.propTypes,
  /**
   * The entity ID we want to link to
   */
  entityId: PropTypes.string.isRequired,
  /**
   * The source entity domain, as used by relationship service.
   */
  entityDomain: PropTypes.string.isRequired,
  /**
   * The source entity type, as used by relationship service.
   */
  entityType: PropTypes.string.isRequired,
  /**
   * The plugins we want to allow linking to
   */
  plugins: PropTypes.arrayOf(pluginShape).isRequired,
  /**
   * Activity variant - whether it's an added or removed
   */
  variant: PropTypes.oneOf(Object.values(ACTIVITY_VARIANTS)),
};

export default ConnectedReferenceActivityCard;
